import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { LandingHeader } from '../components/layout/landing-header';

const TermsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <LandingHeader />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h2>
        
        <div className="prose prose-lg text-gray-600">
          <p className="text-sm text-gray-500 mb-6">Last updated: March 8, 2026</p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h3>
          <p>
            By accessing or using PageMind's services, you agree to be bound by these Terms of Service 
            and all applicable laws and regulations. If you do not agree with any of these terms, you 
            are prohibited from using our service.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Service Description</h3>
          <p>
            PageMind provides an AI-powered chatbot service that integrates with your website to assist 
            visitors through natural language conversations. The service includes:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Automatic content learning from your website</li>
            <li>AI-powered chat interface for visitor interactions</li>
            <li>Analytics and insights dashboard</li>
            <li>Additional features based on your subscription plan</li>
          </ul>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. Account Registration</h3>
          <p>
            To use our service, you must create an account and provide accurate, complete, and current 
            information. You are responsible for safeguarding your account credentials and for all 
            activities that occur under your account.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Subscription and Payment</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Subscription fees are billed in advance on a monthly basis</li>
            <li>All fees are non-refundable except as required by law</li>
            <li>You may cancel your subscription at any time</li>
            <li>Usage limits apply based on your subscription plan</li>
          </ul>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Acceptable Use</h3>
          <p>You agree not to use PageMind to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Violate any laws or regulations</li>
            <li>Infringe on intellectual property rights</li>
            <li>Transmit malicious code or interfere with the service</li>
            <li>Collect personal information without consent</li>
            <li>Create content that is harmful, offensive, or misleading</li>
          </ul>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Intellectual Property</h3>
          <p>
            You retain all rights to your website content. By using our service, you grant PageMind a 
            limited license to process and analyze your content solely for the purpose of providing the 
            service. We do not claim ownership of your content.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by law, PageMind shall not be liable for any indirect, 
            incidental, special, consequential, or punitive damages resulting from your use or inability 
            to use the service.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Service Availability</h3>
          <p>
            We strive to maintain high availability but do not guarantee uninterrupted service. We reserve 
            the right to modify or discontinue the service with reasonable notice.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Termination</h3>
          <p>
            We may terminate or suspend your account for violation of these terms. Upon termination, your 
            right to use the service will cease immediately, and we may delete your data after a reasonable 
            retention period.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Third-Party Integrations</h3>
          <p>
            PageMind offers optional integrations with third-party services, including Google Search Console,
            Google Analytics, Google Ads, and Google Merchant Center. By connecting these services, you
            authorize PageMind to access your data from those platforms in read-only mode and in accordance
            with our <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>. You are
            responsible for ensuring you have the necessary permissions to connect accounts you do not
            personally own. Third-party services are governed by their own terms and privacy policies.
          </p>

          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">11. Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of [Jurisdiction], 
            without regard to its conflict of law provisions.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">12. Changes to Terms</h3>
          <p>
            We reserve the right to modify these terms at any time. We will provide notice of significant 
            changes via email or through the service.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">13. Contact Information</h3>
          <p>
            For questions about these Terms of Service, please contact us at:
          </p>
          <ul className="list-none space-y-1 mt-4">
            <li>Email: legal@pagemind.ai</li>
            <li>Address: [Company Address]</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;